import { Injectable } from "@angular/core";
import {
  OpsIntPromiseLookupService,
  SysFeatures,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
import * as CubiqTS from "../../../shared/cubiq-model-multi-asset";
import {
  BfxBlotterFilter,
  BfxBlotterFilterCondition,
  BfxBlotterFilterId,
  ExtendedOrderTypeIdentifier,
} from "../../bfx-blotter-filter/bfx-blotter-filter.constants";

export enum OpsInterventionFilter {
  API_ENTRY_ALERT = "API Entry Alert",
  BENCHMARKS = "Benchmarks",
  FILL = "Fill",
  MIGRATION = "Migration",
  ORDER_HUB = "Order Hub",
}

export const opsIntFilterList = [
  OpsInterventionFilter.API_ENTRY_ALERT,
  OpsInterventionFilter.BENCHMARKS,
  OpsInterventionFilter.FILL,
  OpsInterventionFilter.MIGRATION,
  OpsInterventionFilter.ORDER_HUB,
];

export const FIELD_NAME = "category";
export const COLUMN_HEADER_NAME = "Category";

@Injectable({
  providedIn: "root",
})
export class OpsIntBlotterFilterListService {
  private static blotterFilterList: Array<BfxBlotterFilter>;
  private filterCategoryIdMap = new Map<string, string>();
  private areBenchmarksEnabled: boolean = true;

  // use filterCategoryId so we can restore from save
  // static generateRandomFilterId(min: number, max: number) {
  // 	return Math.floor(Math.random() * (max - min + 1) + min);
  // }

  /**
   * @description Returns the complete list of category filters for the blotter filter bar.
   */
  public static getAllCategoryFilters(
    service: OpsIntBlotterFilterListService,
  ): Array<BfxBlotterFilter> {
    if (!OpsIntBlotterFilterListService.blotterFilterList) {
      let filterArray = new Array<BfxBlotterFilter>();

      OpsIntBlotterFilterListService.blotterFilterList = opsIntFilterList
        // clear list of bmarks if they're not enabled in system
        .filter((item) =>
          !service.areBenchmarksEnabled && item == OpsInterventionFilter.BENCHMARKS ? false : true,
        )
        .map((item) => service.getCategoryFilterByDescName(item));
    }
    return OpsIntBlotterFilterListService.blotterFilterList;
  }

  constructor(
    protected opsIntLookupService: OpsIntPromiseLookupService,
    protected systemConfigService: SystemConfigPromiseService,
  ) {
    this.systemConfigService
      .getSystemConfig(SysFeatures.benchmarksEnabled)
      .then(
        (benchmarksEnabled) => (this.areBenchmarksEnabled = benchmarksEnabled.toUpperCase() == "Y"),
      );
    this.processFilterCategoryIdMap();
  }

  /**
   * @description Gets correlated category ID for a specific category name.
   * @param filterCategoryDescName Category filter name for differentiation
   */
  public getCategoryId(filterCategoryDescName: string) {
    return this.filterCategoryIdMap.get(filterCategoryDescName);
  }

  /**
   * @description Builds a BfxBlotterFilter.
   * 	The blotter filter contains one filter condition with *Operator.In* for the each represented category.
   * @param filterCategoryDescName Category filter name for differentiation
   */
  protected getCategoryFilterByDescName(filterCategoryDescName: string): BfxBlotterFilter {
    let filter: BfxBlotterFilter = new BfxBlotterFilter(
      this.getCategoryId(filterCategoryDescName),
      filterCategoryDescName,
      null,
      null,
      null,
      false,
    );
    let predefinedByCategory: BfxBlotterFilterCondition = new BfxBlotterFilterCondition(
      FIELD_NAME,
      COLUMN_HEADER_NAME,
      CubiqTS.Operator.In,
      this.getCategoryId(filterCategoryDescName),
      false,
      false,
    );
    filter.filterIdentifier = new BfxBlotterFilterId(
      CubiqTS.BlotterType.OPSINTERVENTION,
      CubiqTS.AssetClass.FIX,
    );
    filter.extendedTypeId = ExtendedOrderTypeIdentifier.OTHER;
    filter.conditions = [predefinedByCategory];

    return filter;
  }

  /**
   * @description This method builds a map to corelate the filter category with it's respected ID from service.
   * Runs asynchronously upon the service initialization.
   */
  private async processFilterCategoryIdMap() {
    let filterCategoryTypeMap: Map<string, CubiqTS.OpsFiltCategoryType> =
      await this.opsIntLookupService.getFilterCategoryTypes();
    let opsIntFilterListValues: string[] = Object.keys(OpsInterventionFilter)
      .map((key) => OpsInterventionFilter[key])
      .filter((k) => !(Number(k) >= 0));
    opsIntFilterListValues.forEach((opsIntDescName: string) => {
      let opsIntFilterCategory = [...filterCategoryTypeMap.values()].find(
        (item: CubiqTS.OpsFiltCategoryType) => {
          let categoryDescName = this.transformCategoryFilterDescName(opsIntDescName);
          return item.filterCatDesc.replace(/\s+/g, "").toLocaleLowerCase() == categoryDescName;
        },
      );
      if (opsIntFilterCategory != null) {
        this.filterCategoryIdMap.set(opsIntDescName, opsIntFilterCategory.filterCategoryId);
      }
    });
  }

  public getCategoryFilterIdMap(): Map<string, string> {
    return this.filterCategoryIdMap;
  }

  /**
   * @description Method for transforming the category name on UI with the correct one coming from server.
   * @param id receives OpsInterventionFilter item
   * @returns correct string from server to match the ones represented in the table.
   */
  public transformCategoryFilterDescName(id: string): string {
    switch (id) {
      case OpsInterventionFilter.API_ENTRY_ALERT:
        return "orderentry";
      case OpsInterventionFilter.BENCHMARKS:
        return "benchmark";
      case OpsInterventionFilter.FILL:
        return "fill";
      case OpsInterventionFilter.MIGRATION:
        return "migration";
      case OpsInterventionFilter.ORDER_HUB:
        return "orderhub";
      default:
        return "-";
    }
  }
}
